import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";
import { LOGO_URL } from "@/pwa/data";
import { login } from "./auth";

const AdminLogin = () => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      setErr("Enter your password.");
      return;
    }
    setErr("");
    setLoading(true);
    try {
      await login(password);
      toast.success("Welcome back, Nikki.");
      nav("/admin/dashboard", { replace: true });
    } catch (e2) {
      const msg = e2?.response?.data?.detail || "Invalid password.";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid={ADMIN.loginPage}
      className="min-h-screen w-full flex items-center justify-center bg-[#121212] text-[#FAF9F6] wood-grain-dark relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-[0.35] pointer-events-none bg-gradient-to-br from-[#5C4033]/50 via-transparent to-[#C19A6B]/20" />
      <div className="relative w-full max-w-md mx-6">
        <div className="text-center mb-10">
          <span className="inline-block w-16 h-16 rounded-full overflow-hidden ring-1 ring-[#C19A6B]/40 bg-[#FAF9F6]">
            <img src={LOGO_URL} alt="Precision Woodworking Artistry" className="w-full h-full object-cover" />
          </span>
          <p className="mt-6 text-[10px] tracking-[0.4em] uppercase text-[#C19A6B] font-semibold">
            Admin · Precision Woodworking
          </p>
          <h1 className="mt-3 font-serif-display text-4xl md:text-5xl leading-[1.04] tracking-tight">
            Welcome back.
          </h1>
          <p className="mt-3 text-[#FAF9F6]/60 text-sm">
            Enter your password to view your leads.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="bg-[#1A1A1A] border border-[#C19A6B]/15 p-8 md:p-10 space-y-6"
        >
          <div>
            <label className="text-[10px] tracking-[0.32em] uppercase text-[#FAF9F6]/55 font-semibold flex items-center gap-2">
              <Lock className="w-3.5 h-3.5" />
              Password
            </label>
            <input
              data-testid={ADMIN.loginPassword}
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your admin password"
              className="pwa-input-dark"
            />
          </div>

          {err && (
            <p
              data-testid={ADMIN.loginError}
              className="text-[#F1B3B3] text-sm border-l-2 border-[#8B3A3A] pl-3"
            >
              {err}
            </p>
          )}

          <button
            data-testid={ADMIN.loginSubmit}
            type="submit"
            disabled={loading}
            className="btn-gold w-full justify-center disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Verifying...
              </>
            ) : (
              <>
                Sign In
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>

          <p className="text-[10px] tracking-wide text-[#FAF9F6]/40 text-center pt-2">
            Session lasts 24 hours. Protected by JWT.
          </p>
        </form>

        <div className="mt-8 text-center">
          <a
            href="/"
            className="text-[11px] tracking-[0.32em] uppercase text-[#FAF9F6]/50 hover:text-[#D9B27F] transition-colors link-line"
          >
            ← Back to website
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
